/**
 * Selective rendering (design doc §11.6): the card always carries the FULL
 * data the tool returned — a resolved argument only ever decides which rows
 * are VISIBLE before a tap, it never filters what the card holds. This module
 * is the pure half of that rule; `CollapsibleRows.tsx` is the render half.
 *
 * **Collapsing is independent of `highlight`** — ruled by the design owner
 * after review (§11.6, corrected): the wave exists to stop a plain "сколько
 * стоят билеты?" from dumping every tariff on screen, and that is the turn
 * with an EMPTY `highlight` (no category named). Gating the collapse on
 * "at least one row highlighted" would leave the complained-about case
 * exactly as it was and fix only the minority that named a tier — missing
 * the wave's own target. `highlight` only ever decides WHICH rows are
 * guaranteed visible, never WHETHER the table collapses.
 *
 * **The threshold is on the HIDDEN count, not the row count**: collapsing
 * earns its tap only when it would hide at least three rows
 * (`MIN_HIDDEN_FOR_DISCLOSURE`) — for the common case (no highlight, or few
 * highlighted rows) that lands at eight rows and up, but it is computed from
 * the actual candidate hidden set, so a table with SIX highlighted rows and
 * two more (hidden would be 2) still renders whole even at eight rows. A
 * disclosure that hides one or two rows costs a tap and saves no screen — an
 * eight-row `ScheduleCard` would otherwise grow a "ещё 2" control on an
 * ordinary opening-hours card.
 *
 * Past that: every HIGHLIGHTED row stays visible — never dropped by
 * collapsing, whatever the count — topped up to five (`VISIBLE_FLOOR`) with
 * the earliest remaining rows in the feed's own order, and the rest goes
 * under "Показать ещё N".
 */

export const MIN_HIDDEN_FOR_DISCLOSURE = 3;
export const VISIBLE_FLOOR = 5;

export type RowSelection<T> = {
  /** Rows to render before any disclosure, in the FEED's own order. */
  visible: T[];
  /** Rows behind "Показать ещё N", in the feed's own order. */
  hidden: T[];
  /** How many rows are highlighted, whole-table — never hidden by collapsing. */
  highlightedCount: number;
};

/** A row's own key is highlighted iff it is non-empty and named by `highlight`. */
export function isRowHighlighted(
  key: string | null | undefined,
  highlight: readonly string[] | null | undefined,
): boolean {
  if (!key) return false;
  if (!highlight || highlight.length === 0) return false;
  return highlight.includes(key);
}

export function selectVisibleRows<T>(
  rows: readonly T[],
  highlight: readonly string[] | null | undefined,
  keyOf: (row: T) => string | null | undefined,
): RowSelection<T> {
  const highlighted = (row: T) => isRowHighlighted(keyOf(row), highlight);
  const highlightedCount = rows.reduce((n, row) => n + (highlighted(row) ? 1 : 0), 0);

  // Every highlighted row first — unconditionally, whatever the count — then
  // the earliest remaining rows top the CANDIDATE visible set up to the floor.
  const candidateVisible = new Set<T>();
  for (const row of rows) {
    if (highlighted(row)) candidateVisible.add(row);
  }
  for (const row of rows) {
    if (candidateVisible.size >= VISIBLE_FLOOR) break;
    candidateVisible.add(row);
  }

  // Collapsing only earns its tap when it would actually hide enough to
  // matter — computed from the CANDIDATE set, so a heavily highlighted table
  // that would hide fewer than the minimum renders whole even past the row
  // count that triggers collapsing in the ordinary (few-highlighted) case.
  const candidateHiddenCount = rows.length - candidateVisible.size;
  if (candidateHiddenCount < MIN_HIDDEN_FOR_DISCLOSURE) {
    return { visible: [...rows], hidden: [], highlightedCount };
  }

  // One pass in the FEED's own order — the set above was built in two passes
  // (highlighted first) and does not preserve it.
  const visible: T[] = [];
  const hidden: T[] = [];
  for (const row of rows) {
    (candidateVisible.has(row) ? visible : hidden).push(row);
  }
  return { visible, hidden, highlightedCount };
}
